import React, {
    useEffect,
    useState,
    memo
  } from "react";
  
  import { db } from "../firebase";
  
  import {
    collection,
    getDocs
  } from "firebase/firestore";
  
  import "./AdminGenerationMatches.css";
  
  /* =========================================================
     CONFIGURATION
  ========================================================= */
  
  const failedLogos = new Set();
  
  const HOST = window.location.hostname;
  
  const BASE_URL =
    HOST === "localhost" ||
    HOST === "127.0.0.1"
      ? "http://localhost:5000"
      : "https://cbsfootball-server.onrender.com";
  
  /* =========================================================
     HELPERS
  ========================================================= */
  
  const normalize = (str) =>
    String(str || "")
      .toLowerCase()
      .replace(/\s+/g, "")
      .replace(/[^a-z0-9]/g, "");
  
  const getTimeValue = (value) => {
    if (!value) return 0;
  
    if (typeof value?.toDate === "function") {
      return value.toDate().getTime();
    }
  
    if (value?.seconds) {
      return value.seconds * 1000;
    }
  
    const parsed = new Date(value).getTime();
  
    return Number.isNaN(parsed) ? 0 : parsed;
  };
  
  const sortByLatest = (a, b) => {
    const t1 =
      getTimeValue(a.createdAt) ||
      getTimeValue(a.matchTimestamp) ||
      0;
  
    const t2 =
      getTimeValue(b.createdAt) ||
      getTimeValue(b.matchTimestamp) ||
      0;
  
    return t2 - t1;
  };
  
  const getInitials = (name) => {
    if (!name) return "FC";
  
    return String(name)
      .trim()
      .split(/\s+/)
      .map((word) => word[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  };
  
  /* =========================================================
     GET MATCH A
  ========================================================= */
  
  const getMatchA = (data = {}) => {
    return (
      data.matchesA ||
      data.matchA ||
      data.A ||
      data.teamA ||
      data.homeTeam ||
      data.home ||
      ""
    );
  };
  
  /* =========================================================
     GET MATCH B
  ========================================================= */
  
  const getMatchB = (data = {}) => {
    return (
      data.matchesB ||
      data.matchB ||
      data.B ||
      data.teamB ||
      data.awayTeam ||
      data.away ||
      ""
    );
  };
  
  /* =========================================================
     LOGO HELPERS
  ========================================================= */
  
  const getServerLogo = (url) => {
    if (!url || failedLogos.has(url)) {
      return "";
    }
  
    return `${BASE_URL}/logo?url=${encodeURIComponent(url)}`;
  };
  
  const getWeservLogo = (url) => {
    if (!url) return "";
  
    if (url.includes("images.weserv.nl")) {
      return url;
    }
  
    return `https://images.weserv.nl/?url=${url.replace(
      /^https?:\/\//,
      ""
    )}&w=160&h=160&fit=contain`;
  };
  
  /* =========================================================
     TEAM LOGO COMPONENT
  ========================================================= */
  
  const Logo = memo(({ src, name }) => {
    const [logoSrc, setLogoSrc] = useState("");
    const [error, setError] = useState(false);
  
    useEffect(() => {
      if (!src || failedLogos.has(src)) {
        setLogoSrc("");
        setError(true);
        return;
      }
  
      setLogoSrc(getServerLogo(src));
      setError(false);
    }, [src]);
  
    if (!src || error || failedLogos.has(src)) {
      return (
        <div className="team-fallback">
          {getInitials(name)}
        </div>
      );
    }
  
    return (
      <img
        src={logoSrc}
        alt={name || "Team logo"}
        className="team-logo"
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => {
          if (
            logoSrc &&
            logoSrc.includes(BASE_URL)
          ) {
            setLogoSrc(src);
            return;
          }
  
          if (logoSrc === src) {
            setLogoSrc(getWeservLogo(src));
            return;
          }
  
          failedLogos.add(src);
          setError(true);
        }}
      />
    );
  });
  
  /* =========================================================
     MAIN COMPONENT
  ========================================================= */
  
  export default function AdminGenerationMatches() {
    const [antBets, setAntBets] = useState([]);
    const [correctBets, setCorrectBets] = useState([]);
  
    const [matchesMap, setMatchesMap] = useState({});
  
    const [loading, setLoading] = useState(true);
  
    const [tab, setTab] = useState("ant");
  
    /* =======================================================
       LOAD FIREBASE DATA
    ======================================================= */
  
    useEffect(() => {
      let mounted = true;
  
      const loadData = async () => {
        try {
          const username =
            localStorage.getItem("username");
  
          /* =================================================
             LOAD MATCHES
          ================================================= */
  
          const matchSnap = await getDocs(
            collection(db, "matches")
          );
  
          const map = {};
  
          matchSnap.docs.forEach((docSnap) => {
            const match = docSnap.data();
  
            const teamA = getMatchA(match);
            const teamB = getMatchB(match);
  
            const key1 =
              `${normalize(teamA)}_${normalize(teamB)}`;
  
            const key2 =
              `${normalize(teamB)}_${normalize(teamA)}`;
  
            map[key1] = match;
  
            map[key2] = {
              ...match,
  
              logoA: match.logoB,
              logoB: match.logoA,
  
              A: teamB,
              B: teamA,
  
              teamA: teamB,
              teamB: teamA,
  
              matchesA: teamB,
              matchesB: teamA
            };
          });
  
          /* =================================================
             LOAD ANTSCORE
          ================================================= */
  
          const antSnap = await getDocs(
            collection(db, "antscore")
          );
  
          /* =================================================
             LOAD CORRECTSCORE
          ================================================= */
  
          const correctSnap = await getDocs(
            collection(db, "correctscore")
          );
  
          /* =================================================
             ANT DATA
          ================================================= */
  
          const antData = antSnap.docs
            .map((docSnap) => ({
              id: docSnap.id,
              ...docSnap.data()
            }))
            .filter((bet) => {
              return (
                !username ||
                bet.username === username
              );
            })
            .filter((bet) => !bet.result)
            .sort(sortByLatest);
  
          /* =================================================
             CORRECT SCORE DATA
          ================================================= */
  
          const correctData = correctSnap.docs
            .map((docSnap) => ({
              id: docSnap.id,
              ...docSnap.data()
            }))
            .filter((bet) => {
              return (
                !username ||
                bet.username === username
              );
            })
            .filter((bet) => !bet.result)
            .sort(sortByLatest);
  
          if (!mounted) return;
  
          setMatchesMap(map);
          setAntBets(antData);
          setCorrectBets(correctData);
  
        } catch (err) {
          console.error(
            "Failed to load admin generation matches:",
            err
          );
        } finally {
          if (mounted) {
            setLoading(false);
          }
        }
      };
  
      loadData();
  
      return () => {
        mounted = false;
      };
    }, []);
  
    /* =======================================================
       FORMAT DATE
    ======================================================= */
  
    const formatMatchDate = (bet) => {
      if (bet.matchTimestamp) {
        const date =
          typeof bet.matchTimestamp.toDate ===
          "function"
            ? bet.matchTimestamp.toDate()
            : new Date(bet.matchTimestamp);
  
        return date.toLocaleDateString("en-GB");
      }
  
      return bet.matchDate || "-";
    };
  
    /* =======================================================
       FORMAT TIME
    ======================================================= */
  
    const formatMatchOnlyTime = (bet) => {
      if (bet.matchTimestamp) {
        const date =
          typeof bet.matchTimestamp.toDate ===
          "function"
            ? bet.matchTimestamp.toDate()
            : new Date(bet.matchTimestamp);
  
        const hours = date.getHours();
  
        const minutes = String(
          date.getMinutes()
        ).padStart(2, "0");
  
        const suffix =
          hours >= 12 ? "PM" : "AM";
  
        return `${String(hours).padStart(
          2,
          "0"
        )}:${minutes} ${suffix}`;
      }
  
      if (bet.matchTime) {
        const raw = String(
          bet.matchTime
        ).trim();
  
        const match = raw.match(
          /^(\d{1,2}):(\d{2})$/
        );
  
        if (match) {
          const hour = Number(match[1]);
  
          const minute = match[2];
  
          const suffix =
            hour >= 12 ? "PM" : "AM";
  
          return `${String(hour).padStart(
            2,
            "0"
          )}:${minute} ${suffix}`;
        }
  
        return raw.toUpperCase();
      }
  
      return "-";
    };
  
    /* =======================================================
       GET MATCH LOGOS
    ======================================================= */
  
    const getLogos = (bet) => {
      const teamA = getMatchA(bet);
  
      const teamB = getMatchB(bet);
  
      const key =
        `${normalize(teamA)}_${normalize(teamB)}`;
  
      const match = matchesMap[key];
  
      return {
        logoA:
          match?.logoA ||
          bet.logoA ||
          null,
  
        logoB:
          match?.logoB ||
          bet.logoB ||
          null
      };
    };
  
    /* =======================================================
       TAB MATCH NAME
    ======================================================= */
  
    const getTabMatchName = (bets) => {
      if (!bets || bets.length === 0) {
        return "No match selected";
      }
  
      const firstBet = bets[0];
  
      const teamA = getMatchA(firstBet);
  
      const teamB = getMatchB(firstBet);
  
      return `${teamA || "Team A"} vs ${
        teamB || "Team B"
      }`;
    };
  
    /* =======================================================
       LOADING
    ======================================================= */
  
    if (loading) {
      return (
        <div className="betslip-page">
  
          <div className="loading-screen">
  
            <img
              src="/logo192.png"
              className="loading-logo"
              alt="Loading"
            />
  
            <p>
              Loading matches...
            </p>
  
          </div>
  
        </div>
      );
    }
  
    /* =======================================================
       ACTIVE LIST
    ======================================================= */
  
    const list =
      tab === "ant"
        ? antBets
        : correctBets;
  
    /* =======================================================
       RENDER
    ======================================================= */
  
    return (
      <div className="betslip-page">
  
        {/* ===================================================
            TABS
        =================================================== */}
  
        <div className="betslip-tabs">
  
          {/* ANT SCORE */}
  
          <button
            type="button"
            className={
              tab === "ant"
                ? "betslip-tab active"
                : "betslip-tab"
            }
            onClick={() => setTab("ant")}
          >
  
            <div className="tab-match-info">
  
              <span>
                AntScore
              </span>
  
              <small>
                {getTabMatchName(antBets)}
              </small>
  
            </div>
  
            <strong>
              {antBets.length}
            </strong>
  
          </button>
  
          {/* CORRECT SCORE */}
  
          <button
            type="button"
            className={
              tab === "correct"
                ? "betslip-tab active"
                : "betslip-tab"
            }
            onClick={() =>
              setTab("correct")
            }
          >
  
            <div className="tab-match-info">
  
              <span>
                CorrectScore
              </span>
  
              <small>
                {getTabMatchName(
                  correctBets
                )}
              </small>
  
            </div>
  
            <strong>
              {correctBets.length}
            </strong>
  
          </button>
  
        </div>
  
        {/* ===================================================
            EMPTY STATE
        =================================================== */}
  
        {list.length === 0 ? (
  
          <div className="no-bets">
  
            <div className="empty-icon">
              ◎
            </div>
  
            <h3>
              No pending selections
            </h3>
  
            <p>
              Your selected matches will appear here.
            </p>
  
          </div>
  
        ) : (
  
          list.map((bet, i) => {
  
            const {
              logoA,
              logoB
            } = getLogos(bet);
  
            const matchesA =
              getMatchA(bet);
  
            const matchesB =
              getMatchB(bet);
  
            return (
  
              <div
                key={bet.id || i}
                className="horizontal-match-card"
              >
  
                {/* =================================================
                    BACKGROUND
                ================================================= */}
  
                <div className="players-background" />
  
                <div className="match-card-content">
  
                  {/* =================================================
                      HEADER
                  ================================================= */}
  
                  <div className="match-card-header">
  
                    <img
                      src="/logo192.png"
                      alt="BFC"
                      className="brand-image"
                    />
  
                    <div className="header-title">
  
                      <strong>
                        B.F.C
                      </strong>
  
                      <span>
                        Choose this SecureScore match.
                        If you lose, receive compensation
                        equal to 100% of your betting stake,
                        subject to the applicable terms.
                      </span>
  
                    </div>
  
                  </div>
  
                  {/* =================================================
                      MATCH INFORMATION
                  ================================================= */}
  
                  <div className="match-information">
  
                    <div className="match-date">
                      {formatMatchDate(bet)}
                    </div>
  
                    <div className="match-teams-banner">
  
                      <span className="match-team-a">
                        {matchesA ||
                          "Matches A"}
                      </span>
  
                      <span className="match-vs-text">
                        vs
                      </span>
  
                      <span className="match-team-b">
                        {matchesB ||
                          "Matches B"}
                      </span>
  
                    </div>
  
                    <div className="match-time-banner">
                      {formatMatchOnlyTime(bet)}
                    </div>
  
                    <div className="match-league-banner">
                      {bet.league ||
                        "Football League"}
                    </div>
  
                  </div>
  
                  {/* =================================================
                      HOME / VS / AWAY
                  ================================================= */}
  
                  <div className="horizontal-teams">
  
                    {/* =================================================
                        HOME TEAM
                    ================================================= */}
  
                    <div className="horizontal-team home-team">
  
                      {/* MATCH A SMALL BANNER */}
  
                      <div className="team-match-banner home-match-banner">
  
                        <span className="team-match-banner-title">
                       
                        </span>
  
                        <span className="team-match-banner-name">
                          {matchesA ||
                            "Matches A"}
                        </span>
  
                      </div>
  
                      {/* LARGE BFC BACKGROUND LOGO */}
  
                      <img
                        src="/logo512.png"
                        alt="BFC"
                        className="team-card-bfc-logo"
                      />
  
                      {/* TEAM LOGO */}
  
                      <div className="team-image-box">
  
                        <div className="ball-background">
  
                          <Logo
                            src={logoA}
                            name={matchesA}
                          />
  
                        </div>
  
                      </div>
  
                      {/* TEAM INFORMATION */}
  
                      <div className="team-details">
  
                        <span className="team-label">
                          HOME
                        </span>
  
                        <strong>
                          {matchesA ||
                            "Matches A"}
                        </strong>
  
                      </div>
  
                      {/* SMALL BFC BADGE */}
  
                      <div className="team-score-badge">
  
                        <img
                          src="/logo512.png"
                          alt="BFC"
                          className="team-score-logo"
                        />
  
                      </div>
  
                    </div>
  
                    {/* =================================================
                        VS
                    ================================================= */}
  
                    <div className="versus-box">
                      VS
                    </div>
  
                    {/* =================================================
                        AWAY TEAM
                    ================================================= */}
  
                    <div className="horizontal-team away-team">
  
                      {/* MATCH B SMALL BANNER */}
  
                      <div className="team-match-banner away-match-banner">
  
                        <span className="team-match-banner-title">
                         
                        </span>
  
                        <span className="team-match-banner-name">
                          {matchesB ||
                            "Matches B"}
                        </span>
  
                      </div>
  
                      {/* LARGE BFC BACKGROUND LOGO */}
  
                      <img
                        src="/logo512.png"
                        alt="BFC"
                        className="team-card-bfc-logo"
                      />
  
                      {/* TEAM LOGO */}
  
                      <div className="team-image-box">
  
                        <div className="ball-background">
  
                          <Logo
                            src={logoB}
                            name={matchesB}
                          />
  
                        </div>
  
                      </div>
  
                      {/* TEAM INFORMATION */}
  
                      <div className="team-details">
  
                        <span className="team-label">
                          AWAY
                        </span>
  
                        <strong>
                          {matchesB ||
                            "Matches B"}
                        </strong>
  
                      </div>
  
                      {/* SMALL BFC BADGE */}
  
                      <div className="team-score-badge">
  
                        <img
                          src="/logo512.png"
                          alt="BFC"
                          className="team-score-logo"
                        />
  
                      </div>
  
                    </div>
  
                  </div>
  
                  {/* =================================================
                      SCORE + ODDS
                  ================================================= */}
  
                  <div className="selection-panel">
  
                    <div className="selection-item score-item">
  
                      <span className="selection-label">
                        SCORE (BAO)
                      </span>
  
                      <strong className="selection-value">
                        {bet.score ||
                          "0 - 0"}
                      </strong>
  
                    </div>
  
                    <div className="selection-item odds-item">
  
                      <span className="selection-label">
                        ODDS (PROFIT)
                      </span>
  
                      <strong className="selection-value">
                        {bet.odds ||
                          "0.00"}%
                      </strong>
  
                    </div>
  
                  </div>
  
                  {/* =================================================
                      FOOTER
                  ================================================= */}
  
                  <div className="card-footer">
  
                    <span>
                      Match details
                    </span>
  
                    <div className="league-green-box">
  
                      {bet.league ||
                        "Football League"}
  
                    </div>
  
                    <span className="footer-mark">
                      B.F.C
                    </span>
  
                  </div>
  
                </div>
  
              </div>
  
            );
          })
  
        )}
  
      </div>
    );
  }